import { ArrowUp, Bug, Loader2, Mic, Square } from 'lucide-react'
import { ATTACH_ACCEPT, pastedImageName } from '@/lib/aiAttachments'
import { cn } from '@/lib/utils'
import { AttachButton, ComposerFileChips } from './attachmentViews'
import { formatElapsed } from './chatRows'
import type { Capabilities, CopilotChat } from './useCopilotChat'

// ── The message box, shared by the bubble and the page ──────────────────────

export function ChatComposer({
  chat,
  caps,
  size = 'sm',
}: {
  chat: CopilotChat
  caps: Capabilities
  /** `md` is the page: a taller box and bigger text. */
  size?: 'sm' | 'md'
}) {
  const { voice, reportMode, setReportMode, input, setInput, submit, canSend, sending, inputRef, focusInput } = chat
  const md = size === 'md'
  const canAttach = caps.files && !reportMode
  return (
    <div className={cn('border-t border-border', md ? 'p-4' : 'p-3')}>
      {voice.isRecording ? (
        <p className="mb-2 flex items-center justify-center gap-1.5 text-xs font-medium text-destructive" role="status">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-destructive" aria-hidden />
          Listening… {formatElapsed(voice.recordingSeconds)}
        </p>
      ) : voice.transcribing ? (
        <p className="mb-2 flex items-center justify-center gap-1.5 text-xs font-medium text-muted-foreground" role="status">
          <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
          Transcribing…
        </p>
      ) : voice.micError ? (
        <p className="mb-2 text-center text-[11px] text-muted-foreground">{voice.micError}</p>
      ) : null}
      {reportMode && (
        <div className="mb-2 flex items-center justify-between gap-2 rounded-md bg-muted px-3 py-1.5 text-[11px] text-foreground">
          <span className="flex items-center gap-1.5">
            <Bug className="h-3.5 w-3.5" aria-hidden /> Reporting a problem — describe it and send.
          </span>
          <button type="button" onClick={() => setReportMode(false)} className="font-medium underline underline-offset-2">
            Cancel
          </button>
        </div>
      )}
      {!reportMode && <ComposerFileChips files={chat.files} onRemove={chat.removeFile} />}
      <div
        className={cn(
          'flex items-end gap-2 rounded-xl border border-input bg-background focus-within:ring-2 focus-within:ring-ring',
          md ? 'px-3 py-2.5' : 'px-2.5 py-2',
        )}
      >
        {canAttach && <AttachButton accept={ATTACH_ACCEPT} disabled={voice.isRecording} onPick={chat.addFiles} />}
        {caps.voiceIn && !reportMode && (
          <button
            type="button"
            onClick={() => (voice.isRecording ? voice.stopRecording() : void voice.startRecording())}
            disabled={(sending && !voice.isRecording) || voice.transcribing}
            aria-label={voice.isRecording ? 'Stop recording' : 'Ask by voice'}
            className={cn(
              'flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-colors disabled:opacity-40',
              voice.isRecording
                ? 'bg-destructive text-destructive-foreground'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground',
            )}
          >
            {voice.isRecording ? (
              <Square className="h-3.5 w-3.5" aria-hidden />
            ) : voice.transcribing ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <Mic className="h-4 w-4" aria-hidden />
            )}
          </button>
        )}
        <textarea
          ref={inputRef}
          rows={1}
          value={input}
          disabled={voice.isRecording}
          aria-label={reportMode ? 'Describe the problem' : 'Message Bhookly AI'}
          onChange={(e) => {
            setInput(e.target.value)
            e.target.style.height = 'auto'
            e.target.style.height = `${Math.min(e.target.scrollHeight, 140)}px`
          }}
          onPaste={(e) => {
            // A pasted screenshot (or copied files) is attached; pasted text stays text.
            if (!canAttach) return
            const pasted = Array.from(e.clipboardData.files)
            if (!pasted.length) return
            e.preventDefault()
            chat.addFiles(
              pasted.map((f, i) => (f.name && f.name !== 'image.png' ? f : new File([f], pastedImageName(f.type, i), { type: f.type }))),
            )
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault()
              if (canSend) submit()
            }
          }}
          placeholder={
            voice.isRecording ? 'Speak now…' : reportMode ? 'Describe the problem…' : 'Message Bhookly AI…'
          }
          className={cn(
            'max-h-[140px] flex-1 resize-none bg-transparent py-1 outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed',
            md ? 'text-base' : 'text-sm',
          )}
        />
        <button
          type="button"
          onClick={submit}
          disabled={!canSend}
          aria-label={reportMode ? 'Send report' : 'Send message'}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-sm transition-opacity disabled:cursor-not-allowed disabled:opacity-40"
        >
          {sending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <ArrowUp className="h-4 w-4" aria-hidden />}
        </button>
      </div>
      <p className="mt-2 text-center text-[11px] text-muted-foreground">
        Bhookly AI can make mistakes — double-check important numbers.
        {caps.report && !reportMode && (
          <>
            {' · '}
            <button
              type="button"
              onClick={() => {
                setReportMode(true)
                focusInput()
              }}
              className="underline underline-offset-2 hover:text-foreground"
            >
              Report a problem
            </button>
          </>
        )}
      </p>
    </div>
  )
}
